import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { loadAllUserBadges } from '../utils/badgeCalculator';

export interface UserStats {
  totalPoints: number;
  weeklyPoints: number;
  monthlyPoints: number;
  practiceStreak: number;
  totalPracticeTime: number;
  weeklyPracticeTime: number;
  badges: string[];
}

class UserStatsService {
  private static instance: UserStatsService;
  private statsCache: Map<string, { stats: UserStats; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  public static getInstance(): UserStatsService {
    if (!UserStatsService.instance) {
      UserStatsService.instance = new UserStatsService();
    }
    return UserStatsService.instance;
  }

  // Calculate streak using the same logic as both components
  private calculateStreak(sessions: any[]): number {
    // Filter sessions with 10+ minutes
    const qualifyingSessions = sessions
      .filter(s => s.durationSec >= 600) // 600 seconds = 10 minutes
      .map(s => {
        const sessionDate = new Date(s.startAt?.toDate?.() || s.endAt?.toDate?.() || 0);
        sessionDate.setHours(0, 0, 0, 0);
        return {
          ...s,
          dateOnly: sessionDate
        };
      })
      .sort((a, b) => b.dateOnly.getTime() - a.dateOnly.getTime());

    // Group sessions by date and find the latest qualifying session per day
    const dailySessions = new Map<string, any>();
    qualifyingSessions.forEach(session => {
      const dateKey = session.dateOnly.toISOString().split('T')[0];
      if (!dailySessions.has(dateKey)) {
        dailySessions.set(dateKey, session);
      }
    });

    // Calculate streak using the same logic as DailyPracticeGoalCard
    let streak = 0;
    const sortedSessions = Array.from(dailySessions.values())
      .sort((a, b) => b.dateOnly.getTime() - a.dateOnly.getTime());
    
    let lastDate: Date | null = null;
    for (const session of sortedSessions) {
      const sessionDate = new Date(session.dateOnly);
      sessionDate.setHours(0, 0, 0, 0);
      
      if (lastDate === null) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((today.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysDiff <= 1) {  // Allow up to 1 day gap (24-hour grace period)
          streak = 1;
          lastDate = sessionDate;
        }
      } else {
        const daysBetween = Math.floor((lastDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysBetween === 1) {  // Consecutive days
          streak++;
          lastDate = sessionDate;
        } else if (daysBetween > 1) {
          break; // Streak broken
        }
      }
    }

    return streak;
  }

  // Calculate points for a user based on their activity
  private async calculateUserPoints(userData: any, sessions: any[], badges: string[]): Promise<{
    totalPoints: number;
    weeklyPoints: number;
    monthlyPoints: number;
    totalPracticeTime: number;
    weeklyPracticeTime: number;
  }> {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    let totalPoints = 0;
    let weeklyPoints = 0;
    let monthlyPoints = 0;
    let totalPracticeTime = 0;
    let weeklyPracticeTime = 0;

    // Process sessions for points calculation (10-minute minimum, one session per day)
    const dailyGoals = new Map<string, number>();
    const weeklySessions = new Map<string, number>();
    const categoryPractice = new Map<string, Set<string>>();
    const sessionPointsByDay = new Map<string, { points: number, sessionType: string }>();

    sessions.forEach(session => {
      const sessionDate = new Date(session.startAt?.toDate?.() || session.endAt?.toDate?.() || 0);
      const dateKey = sessionDate.toISOString().split('T')[0];
      const weekKey = this.getWeekKey(sessionDate);
      
      const durationMinutes = (session.durationSec || 0) / 60;
      totalPracticeTime += durationMinutes;

      if (sessionDate >= weekAgo) {
        weeklyPracticeTime += durationMinutes;
      }

      // Only count sessions over 10 minutes for daily goal calculation
      if (durationMinutes >= 10) {
        // Daily practice goals (using 10-minute minimum for testing)
        const currentDaily = dailyGoals.get(dateKey) || 0;
        dailyGoals.set(dateKey, currentDaily + durationMinutes);

        // Weekly session counting (only sessions over 10 minutes)
        const currentWeekly = weeklySessions.get(weekKey) || 0;
        weeklySessions.set(weekKey, currentWeekly + 1);

        // Session type points (only once per day, highest value wins)
        const sessionType = session.sessionType || 'free_practice';
        const newPoints = sessionType === 'free_practice' ? 3 : 5;
        const currentPoints = sessionPointsByDay.get(dateKey);
        
        if (!currentPoints || newPoints > currentPoints.points) {
          sessionPointsByDay.set(dateKey, {
            points: newPoints,
            sessionType: sessionType
          });
        }
      }

      // Category practice tracking (only for sessions over 10 minutes)
      if (durationMinutes >= 10 && session.categoryTimers) {
        const weekCategoryKey = `${weekKey}_categories`;
        if (!categoryPractice.has(weekCategoryKey)) {
          categoryPractice.set(weekCategoryKey, new Set());
        }
        Object.entries(session.categoryTimers).forEach(([category, timer]: [string, any]) => {
          if (timer.elapsed > 0) {
            categoryPractice.get(weekCategoryKey)!.add(category);
          }
        });
      }
    });

    // Apply session type points (only once per day)
    sessionPointsByDay.forEach((sessionData, dateKey) => {
      const sessionDate = new Date(dateKey);
      totalPoints += sessionData.points;
      if (sessionDate >= weekAgo) weeklyPoints += sessionData.points;
      if (sessionDate >= monthAgo) monthlyPoints += sessionData.points;
    });

    // Daily goal points - use historical daily goals from sessions
    // Group sessions by date and calculate points based on the goal that was active when each session was completed
    const dailyGoalPoints = new Map<string, { points: number; goalUsed: number }>();
    
    sessions.forEach(session => {
      const sessionDate = new Date(session.startAt?.toDate?.() || session.endAt?.toDate?.() || 0);
      const dateKey = sessionDate.toISOString().split('T')[0];
      const durationMinutes = (session.durationSec || 0) / 60;
      
      // Only count sessions over 10 minutes for daily goal calculation
      if (durationMinutes >= 10) {
        // Use the daily goal that was active when this session was completed
        const sessionDailyGoal = session.dailyGoalAtCompletion || userData.practiceSessionDuration || 30;
        
        // Get existing points for this date or initialize
        const existing = dailyGoalPoints.get(dateKey) || { points: 0, goalUsed: sessionDailyGoal };
        
        // Add to total minutes for this date
        const currentTotal = dailyGoals.get(dateKey) || 0;
        dailyGoals.set(dateKey, currentTotal + durationMinutes);
        
        // Update the goal used (should be consistent for all sessions on the same date)
        if (existing.goalUsed !== sessionDailyGoal) {
          console.warn(`Inconsistent daily goals for date ${dateKey}: ${existing.goalUsed} vs ${sessionDailyGoal}`);
        }
        dailyGoalPoints.set(dateKey, { points: existing.points, goalUsed: sessionDailyGoal });
      }
    });
    
    // Calculate points based on historical goals
    dailyGoals.forEach((totalMinutes, date) => {
      const sessionDate = new Date(date);
      const goalData = dailyGoalPoints.get(date);
      const dailyGoalMinimum = goalData?.goalUsed || userData.practiceSessionDuration || 30;
      
      if (totalMinutes >= dailyGoalMinimum) {
        totalPoints += 10;
        if (sessionDate >= weekAgo) weeklyPoints += 10;
        if (sessionDate >= monthAgo) monthlyPoints += 10;
      } else if (totalMinutes >= dailyGoalMinimum * 0.5) {
        totalPoints += 5;
        if (sessionDate >= weekAgo) weeklyPoints += 5;
        if (sessionDate >= monthAgo) monthlyPoints += 5;
      }
    });

    // Weekly frequency goals
    weeklySessions.forEach((sessionCount, week) => {
      const weeklyGoal = userData.practiceSessionsPerWeek || 3;
      const weekDate = new Date(week);
      
      let points = 0;
      if (sessionCount >= weeklyGoal) {
        if (weeklyGoal <= 2) points = 5;
        else if (weeklyGoal <= 4) points = 10;
        else points = 20;
      }

      totalPoints += points;
      if (weekDate >= weekAgo) weeklyPoints += points;
      if (weekDate >= monthAgo) monthlyPoints += points;
    });

    // Multi-category practice points
    categoryPractice.forEach((categories, weekKey) => {
      if (categories.size >= 3) {
        const weekDate = new Date(weekKey.split('_')[0]);
        totalPoints += 5;
        if (weekDate >= weekAgo) weeklyPoints += 5;
        if (weekDate >= monthAgo) monthlyPoints += 5;
      }
    });

    // Badge points
    badges.forEach(badge => {
      totalPoints += 5; // All badges give 5 points
      weeklyPoints += 5;
      monthlyPoints += 5;
    });

    return {
      totalPoints,
      weeklyPoints,
      monthlyPoints,
      totalPracticeTime,
      weeklyPracticeTime
    };
  }

  private getWeekKey(date: Date): string {
    const year = date.getFullYear();
    const week = this.getWeekNumber(date);
    return `${year}-W${week}`;
  }

  private getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  // Get user statistics with caching
  public async getUserStats(userId: string, forceRefresh: boolean = false): Promise<UserStats> {
    const cacheKey = userId;
    const now = Date.now();

    // Check cache first
    if (!forceRefresh && this.statsCache.has(cacheKey)) {
      const cached = this.statsCache.get(cacheKey)!;
      if (now - cached.timestamp < this.CACHE_DURATION) {
        return cached.stats;
      }
    }

    try {
      // Get user data
      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
      if (userDoc.empty) {
        throw new Error('User not found');
      }
      const userData = userDoc.docs[0].data();

      // Check if we need to migrate sessions for this user
      // This is a one-time check that will be removed in a future version
      await this.checkAndMigrateSessions(userId);

      // Get user's practice sessions
      const sessionsSnapshot = await getDocs(
        query(
          collection(db, 'practiceSessions'),
          where('studentId', '==', userId)
        )
      );
      const sessions = sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Get user's badges
      const badges = await loadAllUserBadges(userId, db);

      // Calculate streak
      const practiceStreak = this.calculateStreak(sessions);

      // Calculate points
      const pointsData = await this.calculateUserPoints(userData, sessions, badges);

      // Apply streak multiplier
      let multiplier = 1;
      if (practiceStreak >= 100) multiplier = 2.0;
      else if (practiceStreak >= 30) multiplier = 1.5;
      else if (practiceStreak >= 7) multiplier = 1.2;

      const stats: UserStats = {
        totalPoints: Math.round(pointsData.totalPoints * multiplier),
        weeklyPoints: Math.round(pointsData.weeklyPoints * multiplier),
        monthlyPoints: Math.round(pointsData.monthlyPoints * multiplier),
        practiceStreak,
        totalPracticeTime: pointsData.totalPracticeTime,
        weeklyPracticeTime: pointsData.weeklyPracticeTime,
        badges
      };

      // Cache the result
      this.statsCache.set(cacheKey, { stats, timestamp: now });

      return stats;
    } catch (error) {
      console.error('Error calculating user stats:', error);
      throw error;
    }
  }

  // Clear cache for a specific user or all users
  public clearCache(userId?: string): void {
    if (userId) {
      this.statsCache.delete(userId);
    } else {
      this.statsCache.clear();
    }
  }

  // Get just the streak for a user (lightweight)
  public async getUserStreak(userId: string): Promise<number> {
    const stats = await this.getUserStats(userId);
    return stats.practiceStreak;
  }

  // Check if sessions need migration and migrate if necessary
  private async checkAndMigrateSessions(userId: string): Promise<void> {
    try {
      // Check if we've already migrated this user
      const migrationKey = `migrated_${userId}`;
      if (localStorage.getItem(migrationKey)) {
        return; // Already migrated
      }

      // Check if any sessions need migration
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', userId),
        limit(1) // Just check if any exist without the field
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      let needsMigration = false;
      
      sessionsSnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        if (!data.dailyGoalAtCompletion) {
          needsMigration = true;
        }
      });
      
      if (needsMigration) {
        console.log(`Migrating sessions for user ${userId}...`);
        await this.migrateSessionsWithDailyGoal(userId);
        localStorage.setItem(migrationKey, 'true');
      }
    } catch (error) {
      console.error('Error checking migration status:', error);
    }
  }

  // Migration function to add dailyGoalAtCompletion to existing sessions
  public async migrateSessionsWithDailyGoal(userId: string): Promise<void> {
    try {
      const { collection, query, where, getDocs, updateDoc, doc } = await import('firebase/firestore');
      
      // Get all sessions for this user that don't have dailyGoalAtCompletion
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', userId)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsToUpdate: any[] = [];
      
      sessionsSnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        if (!data.dailyGoalAtCompletion) {
          sessionsToUpdate.push({
            id: docSnapshot.id,
            data: data
          });
        }
      });
      
      if (sessionsToUpdate.length === 0) {
        console.log(`No sessions need migration for user ${userId}`);
        return;
      }
      
      console.log(`Migrating ${sessionsToUpdate.length} sessions for user ${userId}`);
      
      // Get user's current daily goal
      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
      let currentDailyGoal = 30; // Default
      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data();
        currentDailyGoal = userData.practiceSessionDuration || 30;
      }
      
      // Update sessions with the current daily goal
      // Note: This is a best-effort migration - we use the current goal as a fallback
      for (const session of sessionsToUpdate) {
        try {
          await updateDoc(doc(db, 'practiceSessions', session.id), {
            dailyGoalAtCompletion: currentDailyGoal,
            sessionType: session.data.lessonId === 'free_practice' ? 'free_practice' : 'guided'
          });
        } catch (error) {
          console.error(`Failed to migrate session ${session.id}:`, error);
        }
      }
      
      console.log(`Migration completed for user ${userId}`);
    } catch (error) {
      console.error('Error during session migration:', error);
    }
  }
}

export default UserStatsService;
